// CS-1732 & CS-1845: Consolidated order calculation component.
// Replaces LiveOrderSummary and internalizes orderPricingMath logic.
import { Box, Chip, Divider, Typography, useTheme, keyframes } from "@mui/material";
import {
  AttachMoneyOutlined,
  BoltOutlined,
  CheckOutlined,
  CancelOutlined,
  CreditCardOutlined,
  Inventory2Outlined,
  ReceiptLongOutlined,
  ViewInArOutlined,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { tokens } from "../../../theme";
import Button from "../../common/Button";
import { useModalStyles } from "../../common/useModalStyles";
import { SectionHeader } from "../../ui/ContentSection";

// --- HELPERS & TYPES ---

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const formatCurrency = (value: number) =>
  `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export interface SkuAllowablePrice {
  productItemId?: number;
  sku?: string;
  size?: string;
  allowablePrice?: number | null;
  extendedPrice?: number | null;
  upfrontPrice?: number | null;
  [key: string]: any;
}

export type ProductItemForPricing = {
  productItemId: number;
  size: string;
  productItemDescription?: string | null;
  units?: string | number | null;
};

// --- COMPONENT ---

interface OrderCalculationCardProps {
  // Common data
  productName?: string;
  manufacturerName?: string | null;
  providerSubtitle?: string | null;
  productItems?: ProductItemForPricing[];
  quantities: { [productItemId: number]: number };
  extendedPrice?: number | null;
  upfrontPrice?: number | null;
  hasSku?: boolean;
  skuAllowablePrices?: SkuAllowablePrice[] | null;
  shippingCost?: number | null;
  shippingLabel?: string | null;
  // CS-1766 - Price the practice would have paid, shown struck through when free shipping waives it.
  shippingWaivedAmount?: number | null;
  discountPercent?: number;
  /** Outside-order edit: per-item unit prices saved on the order (orderItemPrice). */
  savedOrderItemPrices?: Record<number, number> | null;

  // Layout & UI Modes
  layout?: "sidebar" | "card"; // sidebar = high-density LiveOrderSummary style; card = standalone summary box
  showUpfrontOrderUi?: boolean; // When true, enables the Pay Now/Later toggle (Sidebar mode)
  useUpfrontPrice?: boolean;   // When true, forces the card to show upfront pricing directly (Card mode)
  allowEmptyItems?: boolean;   // When true, allows submission even when there are no active items (for outside manual orders)
  manualOrderPrice?: number | null; // Manual order price to display when there are no items
  
  // Customization (mostly for Card mode)
  title?: string;
  badgeText?: string;
  accentColor?: string;
  totalLabel?: string;
  actionLabel?: string;
  payLaterActionLabel?: string;
  /** Sidebar: primary button label when upfront UI is hidden (defaults to "Submit"). */
  submitActionLabel?: string;
  actionIcon?: React.ReactNode;
  fullHeight?: boolean;

  // Callbacks
  onAction?: () => void | Promise<void>;
  onPayNow?: () => void | Promise<void>;
  onPayLater?: () => void | Promise<void>;
  onTotalCalculated?: (total: number) => void;
  /** Sidebar: notifies parent when Pay Now / Pay Later selection changes (null = not chosen yet). */
  onPaymentModeChange?: (mode: "now" | "later" | null) => void;
  /** Sidebar: full-width cancel under the primary action (e.g. navigate back). */
  onCancel?: () => void;
}

const OrderCalculationCard = ({
  productName = "",
  manufacturerName,
  providerSubtitle,
  productItems = [],
  quantities,
  extendedPrice,
  upfrontPrice,
  hasSku = true,
  skuAllowablePrices = null,
  shippingCost = null,
  shippingLabel,
  shippingWaivedAmount = null,
  discountPercent = 0,
  savedOrderItemPrices = null,
  layout = "card",
  showUpfrontOrderUi = false,
  useUpfrontPrice = false,
  allowEmptyItems = false,
  manualOrderPrice,
  title,
  badgeText,
  accentColor,
  totalLabel = "Total",
  actionLabel,
  payLaterActionLabel,
  submitActionLabel,
  actionIcon,
  fullHeight = false,
  onAction,
  onPayNow,
  onPayLater,
  onTotalCalculated,
  onPaymentModeChange,
  onCancel,
}: OrderCalculationCardProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { tokens: mTokens, primaryBtnSx, cancelBtnSx } = useModalStyles();
  const cardSx = { borderRadius: "16px", border: `1px solid ${mTokens.border}`, backgroundColor: mTokens.bg, backgroundImage: "none", overflow: "visible",mb:2 };

  // Sidebar specific styling
  const sidebarAccent = mTokens.accent;
  const sidebarAccentSoft = isDark ? "rgba(20, 184, 166, 0.22)" : "rgba(0, 120, 103, 0.18)";
  const headerBarBg = isDark
    ? "linear-gradient(135deg, rgba(20, 184, 166, 0.22), rgba(20, 184, 166, 0.06))"
    : "linear-gradient(135deg, rgba(0, 120, 103, 0.18), rgba(0, 120, 103, 0.06))";

  // Card specific styling
  const resolvedAccentColor = accentColor || colors.primary[100];
  const cardTitleColor = accentColor || (useUpfrontPrice ? colors.success[300] : colors.grey[100]);
  const regularCardBorderColor = isDark ? theme.palette.common.white : theme.palette.text.primary;
  
  // Shared divider style
  const dividerSx = {
    borderColor: isDark ? "rgba(255, 255, 255, 0.18)" : colors.grey[400],
    opacity: 1,
    my: layout === "sidebar" ? 0 : 2
  };

  // --- LOGIC: PAYMENT MODE (Sidebar only) ---
  const [paymentMode, setPaymentMode] = useState<"later" | "now" | null>(null);

  // Reset payment mode whenever the upfront UI availability changes, the product changes,
  // or all quantities are cleared (user removed all sizes).
  const totalQuantity = useMemo(
    () => Object.values(quantities).reduce((sum, q) => sum + (q || 0), 0),
    [quantities]
  );

  useEffect(() => {
    if (layout === "sidebar") {
      setPaymentMode(null);
    }
  }, [showUpfrontOrderUi, layout, productName, totalQuantity]);

  useEffect(() => {
    if (layout === "sidebar") {
      onPaymentModeChange?.(paymentMode);
    }
  }, [layout, paymentMode, onPaymentModeChange]);

  // --- LOGIC: PRICING MATH (Internalized) ---
  const skuPriceLookup = useMemo(() => {
    if (!skuAllowablePrices || skuAllowablePrices.length === 0) return null;
    const byProductItemId: { [id: number]: { extended: number; upfront: number } } = {};
    const bySize: { [size: string]: { extended: number; upfront: number } } = {};
    skuAllowablePrices.forEach((entry) => {
      const extPrice = entry.extendedPrice ?? entry.allowablePrice ?? 0;
      let upfPrice = entry.upfrontPrice;
      if (upfPrice === null || upfPrice === undefined) {
        if (entry.upfrontDiscount != null && entry.upfrontDiscount > 0) {
          upfPrice = extPrice * (1 - (entry.upfrontDiscount / 100));
        } else {
          upfPrice = extPrice;
        }
      }
      if (entry.productItemId != null) {
        byProductItemId[Number(entry.productItemId)] = { extended: Number(extPrice), upfront: Number(upfPrice) };
      }
      if (entry.size != null) {
        bySize[String(entry.size)] = { extended: Number(extPrice), upfront: Number(upfPrice) };
      }
    });
    return (Object.keys(byProductItemId).length > 0 || Object.keys(bySize).length > 0)
      ? { byProductItemId, bySize }
      : null;
  }, [skuAllowablePrices]);

  // CS-1732: Resolve price for a given productItem.
  // hasSku=true  → look up per-item SKU price (unit price, qty applied at call site)
  // hasSku=false → extendedPrice * units (already accounts for units)
  const getItemPrice = useCallback((productItemId: number, size: string, units: number, type: "extended" | "upfront" = "extended"): number => {
    if (type === "extended" && savedOrderItemPrices?.[productItemId] != null) {
      return savedOrderItemPrices[productItemId];
    }
    if (hasSku) {
      const prices = skuPriceLookup?.byProductItemId?.[productItemId] ?? skuPriceLookup?.bySize?.[size];
      if (prices) {
        return type === "upfront" ? prices.upfront : prices.extended;
      }
      const basePrice = type === "upfront" ? (upfrontPrice ?? extendedPrice ?? 0) : (extendedPrice ?? 0);
      return basePrice;
    }
    const basePrice = type === "upfront" ? (upfrontPrice ?? extendedPrice ?? 0) : (extendedPrice ?? 0);
    return basePrice * units;
  }, [hasSku, skuPriceLookup, extendedPrice, upfrontPrice, savedOrderItemPrices]);

  const activeItems = useMemo(
    () => productItems.filter((item) => (quantities[item.productItemId] || 0) > 0),
    [productItems, quantities]
  );

  const regularSubtotal = useMemo(() => {
    // Use manual order price when there are no active items and allowEmptyItems is true
    if (allowEmptyItems && activeItems.length === 0 && manualOrderPrice != null) {
      return manualOrderPrice;
    }
    return productItems.reduce((sum, item) => {
      const qty = quantities[item.productItemId] || 0;
      const units = Number(item.units) || 1;
      return sum + getItemPrice(item.productItemId, item.size, units, "extended") * qty;
    }, 0);
  }, [getItemPrice, quantities, productItems, allowEmptyItems, activeItems.length, manualOrderPrice]);

  const upfrontSubtotal = useMemo(() => {
    // Use manual order price when there are no active items and allowEmptyItems is true
    if (allowEmptyItems && activeItems.length === 0 && manualOrderPrice != null) {
      return manualOrderPrice;
    }
    return productItems.reduce((sum, item) => {
      const qty = quantities[item.productItemId] || 0;
      const units = Number(item.units) || 1;
      return sum + getItemPrice(item.productItemId, item.size, units, "upfront") * qty;
    }, 0);
  }, [getItemPrice, quantities, productItems, allowEmptyItems, activeItems.length, manualOrderPrice]);

  const ship = shippingCost ?? 0;
  // Pay later / "regular" = sum of extended SKU (or flat extended) line totals + ship — same as legacy Regular Price card.
  // discountPercent is upfrontDiscount from the agreement (Save X% copy); it must NOT reduce pay-later money totals
  // (that would double-discount vs line-item extended vs upfront prices).
  const payLaterOrderTotal = regularSubtotal + ship;
  const upfrontTotal = upfrontSubtotal + ship;
  const upfrontSavings = Math.max(0, regularSubtotal - upfrontSubtotal);

  // --- RESOLVED DISPLAY VALUES ---
  const isUpfrontActive = layout === "sidebar" ? (showUpfrontOrderUi && paymentMode === "now") : useUpfrontPrice;

  const discountedSubtotal = useMemo(
    () => (isUpfrontActive ? upfrontSubtotal : regularSubtotal),
    [isUpfrontActive, upfrontSubtotal, regularSubtotal]
  );

  const finalTotal = discountedSubtotal + ship;
  const finalSavings = regularSubtotal - discountedSubtotal;

  const upfrontDiscountPctLabel =
    discountPercent > 0 && Number.isFinite(discountPercent)
      ? ` (${Math.round(discountPercent) === discountPercent ? Math.round(discountPercent) : Number(discountPercent.toFixed(1))}%)`
      : "";

  useEffect(() => {
    if (onTotalCalculated) {
      // Sidebar mode: only report total if a payment mode is chosen (if UI is shown)
      if (layout === "sidebar" && showUpfrontOrderUi && paymentMode === null) {
        onTotalCalculated(0);
      } else {
        onTotalCalculated(finalTotal);
      }
    }
  }, [finalTotal, onTotalCalculated, layout, showUpfrontOrderUi, paymentMode]);

  const bylineName = (manufacturerName ?? "").trim() || (providerSubtitle ?? "").trim() || null;
  const shippingSummaryLabel = useMemo(() => {
    if (!shippingLabel?.trim()) return "Shipping";
    // Strip leading currency pattern like "$15.00 " or "$15 " to avoid double-display
    const cleaned = shippingLabel.trim().replace(/^\$\d+(\.\d{2})?\s*/, "");
    return cleaned ? `Shipping · ${cleaned}` : "Shipping";
  }, [shippingLabel]);

  const handlePrimaryClick = () => {
    if (layout === "sidebar") {
      if (!allowEmptyItems && activeItems.length === 0) return;
      if (!showUpfrontOrderUi) {
        onPayLater?.();
      } else if (paymentMode === "now") {
        onPayNow?.();
      } else if (paymentMode === "later") {
        onPayLater?.();
      }
    } else {
      onAction?.();
    }
  };

  // --- HELPERS ---

  const PayPick = ({ selected, isPayNowOption, onSelect, icon, title: pickTitle, subtitle, badge, amount }: any) => (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      sx={{
        display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.25 }, p: { xs: 1.25, sm: 1.5 }, borderRadius: 2.5, width: "100%", textAlign: "left", cursor: "pointer",
        border: `1px solid ${selected ? (isPayNowOption ? sidebarAccent : colors.grey[400]) : "rgba(255, 255, 255, 0.08)"}`,
        bgcolor: selected 
          ? (isPayNowOption ? `${sidebarAccent}12` : colors.grey[800]) 
          : "rgba(255, 255, 255, 0.03)", 
        color: "inherit", transition: "all 0.2s",
        boxShadow: selected && isPayNowOption ? `0 0 0 1px ${sidebarAccent}30` : "none",
        "&:hover": { bgcolor: selected ? undefined : "rgba(255, 255, 255, 0.06)" },
      }}
    >
      <Box sx={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${selected ? sidebarAccent : colors.grey[700]}`, display: "grid", placeItems: "center", flexShrink: 0 }}>
        {selected && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: sidebarAccent }} />}
      </Box>
      <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: colors.grey[800], display: "grid", placeItems: "center", color: selected && isPayNowOption ? sidebarAccent : colors.grey[400], flexShrink: 0 }}>
        {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 16 } })}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 600, fontSize: { xs: 13, sm: 14 }, color: colors.grey[100], lineHeight: 1.2 }}>{pickTitle}</Typography>
          {badge && (
            <Typography component="span" sx={{ fontSize: 8, fontWeight: 800, px: 0.6, py: 0.1, borderRadius: 10, bgcolor: sidebarAccent, color: isDark ? "#0a0e13" : "#ffffff", textTransform: "uppercase", letterSpacing: 0.2 }}>
              {badge}
            </Typography>
          )}
        </Box>
        {subtitle && <Typography sx={{ fontSize: 10, color: colors.grey[500], mt: 0.2, lineHeight: 1.1 }}>{subtitle}</Typography>}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 15 }, color: colors.grey[100], fontVariantNumeric: "tabular-nums", flexShrink: 0, ml: 1 }}>
        {formatCurrency(amount)}
      </Typography>
    </Box>
  );

  // --- RENDER ---

  // CS-1845 - High-density sidebar render logic
  if (layout === "sidebar") {
    const needsPaymentChoice = showUpfrontOrderUi && paymentMode === null;
    const primaryDisabled = (!allowEmptyItems && activeItems.length === 0) || needsPaymentChoice;
    const sidebarPrimaryLabel = !showUpfrontOrderUi 
      ? (submitActionLabel || "Submit")
      : needsPaymentChoice 
        ? "Select payment method" 
        : paymentMode === "now" 
          ? "Pay & place order" 
          : (payLaterActionLabel || "Place order (pay later)");

    return (
      <Box sx={cardSx}>
        <SectionHeader icon={<AttachMoneyOutlined />} iconColor="#D97706" title="Order Summary" />
        <Box sx={{ px: 2.8, py: 1 }}>

          {activeItems.length > 0 ? (
            <Box sx={{ pb: 0.5 }}>
              {activeItems.map((item) => {
                const qty = quantities[item.productItemId] || 0;
                const units = Number(item.units) || 1;
                const extUnit = getItemPrice(item.productItemId, item.size, units, "extended");
                const extLine = extUnit * qty;
                return (
                  <Box key={item.productItemId} display="flex" alignItems="center" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                      <Box sx={{ px: 0.8, py: 0.2, borderRadius: "6px", backgroundColor: mTokens.cardBg, border: `1px solid ${mTokens.border}` }}>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: mTokens.textSecondary }}>×{qty}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: mTokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.size}{item.productItemDescription ? ` · ${item.productItemDescription}` : ""}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: mTokens.textPrimary, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                      {formatCurrency(extLine)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box display="flex" alignItems="center" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
              <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary }}>No sizes selected yet</Typography>
            </Box>
          )}

          {showUpfrontOrderUi && (
            <>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.1em", pt: 1.2, pb: 0.8 }}>Payment Method</Typography>
              <Box onClick={() => setPaymentMode("now")}
                sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.4, py: 1, borderRadius: "10px", border: `1px solid ${paymentMode === "now" ? mTokens.accent : mTokens.border}`, backgroundColor: paymentMode === "now" ? `${mTokens.accent}0d` : "transparent", cursor: "pointer", mb: 0.8, transition: "border-color 0.18s, background-color 0.18s" }}>
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", border: `2px solid ${paymentMode === "now" ? mTokens.accent : mTokens.inputBorder}`, backgroundColor: paymentMode === "now" ? mTokens.accent : "transparent", flexShrink: 0, transition: "all 0.18s" }} />
                <Box sx={{ width: 28, height: 28, borderRadius: "7px", backgroundColor: mTokens.cardBg, border: `1px solid ${mTokens.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <BoltOutlined sx={{ fontSize: "0.9rem", color: "#f59e0b" }} />
                </Box>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: mTokens.textPrimary, flex: 1 }}>Pay Now</Typography>
                {discountPercent > 0 && <Chip label={`SAVE ${Math.round(discountPercent)}%`} size="small" sx={{ backgroundColor: `${mTokens.accent}22`, color: mTokens.accent, border: `1px solid ${mTokens.accent}44`, fontWeight: 700, fontSize: "0.6rem", height: 18 }} />}
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: mTokens.textPrimary, ml: 0.5 }}>{formatCurrency(upfrontTotal)}</Typography>
              </Box>
              <Box onClick={() => setPaymentMode("later")}
                sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.4, py: 1, borderRadius: "10px", border: `1px solid ${paymentMode === "later" ? mTokens.accent : mTokens.border}`, backgroundColor: paymentMode === "later" ? `${mTokens.accent}0d` : "transparent", cursor: "pointer", mb: 1, transition: "border-color 0.18s, background-color 0.18s" }}>
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", border: `2px solid ${paymentMode === "later" ? mTokens.accent : mTokens.inputBorder}`, backgroundColor: paymentMode === "later" ? mTokens.accent : "transparent", flexShrink: 0, transition: "all 0.18s" }} />
                <Box sx={{ width: 28, height: 28, borderRadius: "7px", backgroundColor: mTokens.cardBg, border: `1px solid ${mTokens.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <ReceiptLongOutlined sx={{ fontSize: "0.9rem", color: mTokens.textSecondary }} />
                </Box>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: mTokens.textPrimary, flex: 1 }}>Pay Later</Typography>
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: mTokens.textPrimary }}>{formatCurrency(payLaterOrderTotal)}</Typography>
              </Box>
            </>
          )}

          <Box display="flex" alignItems="center" justifyContent="space-between" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
            <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary }}>Subtotal</Typography>
            <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 500 }}>{formatCurrency(discountedSubtotal)}</Typography>
          </Box>
          {showUpfrontOrderUi && upfrontSavings > 0 && paymentMode === "now" && (
            <Box display="flex" alignItems="center" justifyContent="space-between" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
              <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary }}>Discount{upfrontDiscountPctLabel}</Typography>
              <Typography sx={{ fontSize: "0.88rem", color: mTokens.accent, fontWeight: 600 }}>−{formatCurrency(upfrontSavings)}</Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
            <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary }}>{shippingSummaryLabel}</Typography>
            {/* CS-1766 - Free shipping: show what the charge would have been, struck through, with a
                "Waived off" note. The order is still charged the zero shown in the total. */}
            {shippingWaivedAmount != null && shippingWaivedAmount > 0 ? (
              <Box display="flex" alignItems="center" gap={0.9}>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 800,
                    lineHeight: 1.6,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#ef4444",
                    backgroundColor: "rgba(239,68,68,0.12)",
                    border: "1px solid rgba(239,68,68,0.3)",
                    borderRadius: "999px",
                    px: 0.9,
                    flexShrink: 0,
                  }}
                >
                  Waived
                </Typography>
                {/* The old price recedes: muted text with a hairline strike, so the digits stay
                    readable instead of being buried under a heavy line. */}
                <Typography
                  // sx={{
                  //   fontSize: "0.88rem",
                  //   color: mTokens.textPrimary,
                  //   fontWeight: 600,
                  //   textDecoration: "line-through",
                  //   textDecorationThickness: "0.5px",
                  //   textDecorationColor: "#ef4444",
                  // }}
                  sx={{
                    fontSize: "1rem",
                    color: mTokens.textSecondary,
                    fontWeight: 600,
                    textDecoration: "line-through",
                    textDecorationThickness: "1.5px",
                    textDecorationColor: "rgba(239,68,68,0.75)",
                    textDecorationSkipInk: "none",
                  }}
                >
                  {formatCurrency(shippingWaivedAmount)}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 500 }}>{formatCurrency(ship)}</Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" py={1}>
            <Typography sx={{ fontSize: "0.9rem", color: mTokens.textPrimary, fontWeight: 700 }}>{(!showUpfrontOrderUi || paymentMode === "later" || paymentMode === null) ? "Total due" : "You pay now"}</Typography>
            <Typography sx={{ fontSize: "0.9rem", color: mTokens.textPrimary, fontWeight: 800 }}>{(showUpfrontOrderUi && paymentMode === null) ? "—" : formatCurrency(finalTotal)}</Typography>
          </Box>

          {needsPaymentChoice && (
            <Typography sx={{ fontSize: "0.75rem", color: mTokens.textSecondary, textAlign: "center", mb: 1 }}>Choose Pay Now or Pay Later to continue</Typography>
          )}

          <Box display="flex" gap={1.5} mt={0.5}>
            {onCancel && (
              <Button fullWidth variant="outlined" type="button" onClick={onCancel} removeUpperCase sx={cancelBtnSx}>Cancel</Button>
            )}
            <Button
              fullWidth
              disabled={primaryDisabled}
              onClick={handlePrimaryClick}
              removeUpperCase
              startIcon={<CheckOutlined sx={{ fontSize: "1rem !important" }} />}
              sx={{
                ...primaryBtnSx,
                backgroundColor: primaryDisabled ? undefined : mTokens.accent,
                boxShadow: primaryDisabled ? "none" : "0 4px 18px rgba(20,184,166,.35)",
                // CS: enhanced disabled styling — keep the label readable and
                // clearly communicate the disabled state within the theme.
                "&.Mui-disabled": {
                  background: mTokens.cardBg,
                  border: `1px solid ${mTokens.inputBorder}`,
                  boxShadow: "none",
                  opacity: 1,
                  cursor: "not-allowed",
                  pointerEvents: "auto",
                  "& .MuiTypography-root": { color: `${mTokens.textSecondary} !important` },
                  "& .MuiButton-startIcon": { color: mTokens.textSecondary },
                },
              }}
            >
              {sidebarPrimaryLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // --- RENDER: CARD MODE (Standalone card style) ---
  return (
    <Box sx={{ backgroundColor: colors.grey[900], borderRadius: 3, p: 2.5, border: `1px solid ${useUpfrontPrice ? resolvedAccentColor : regularCardBorderColor}`, position: "relative", ...(fullHeight && { height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box" }) }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {useUpfrontPrice && <BoltOutlined sx={{ color: resolvedAccentColor, fontSize: 20 }} />}
          <Typography sx={{ color: cardTitleColor, fontWeight: "bold", fontSize: "1rem" }}>{title || "Order Summary"}</Typography>
        </Box>
        {badgeText && <Typography sx={{ color: cardTitleColor, backgroundColor: `${cardTitleColor}15`, border: `1px solid ${cardTitleColor}50`, borderRadius: "12px", px: 1.5, py: 0.4, fontSize: "0.75rem", fontWeight: 700 }}>{badgeText}</Typography>}
      </Box>

      {activeItems.length > 0 && (
        <>
          <Typography sx={{ color: colors.grey[500], fontSize: "0.75rem", fontWeight: 700, mb: 1.5, textTransform: "uppercase" }}>Breakdown</Typography>
          {activeItems.map((item) => {
            const qty = quantities[item.productItemId] || 0;
            const units = Number(item.units) || 1;
            const extUnit = getItemPrice(item.productItemId, item.size, units, "extended");
            const extLine = extUnit * qty;
            const label = item.productItemDescription ? `${item.size} - ${item.productItemDescription}` : item.size;
            return (
              <Box key={item.productItemId} sx={{ display: "flex", justifyContent: "space-between", mb: 1, gap: 1, alignItems: "flex-start" }}>
                <Typography sx={{ color: colors.grey[400], fontSize: "0.85rem", minWidth: 0, pr: 1 }}>{label} × {qty}</Typography>
                <Typography sx={{ color: colors.grey[100], fontSize: "0.8rem", fontWeight: 600, textAlign: "right", flexShrink: 0, maxWidth: "58%", lineHeight: 1.35 }}>
                  {formatCurrency(extLine)}
                </Typography>
              </Box>
            );
          })}
          <Divider sx={{ borderColor: colors.grey[800], my: 2 }} />
        </>
      )}

      {useUpfrontPrice && upfrontSavings > 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ color: resolvedAccentColor, fontSize: "0.95rem", fontWeight: 600 }}>Upfront discount{upfrontDiscountPctLabel}</Typography>
          <Typography sx={{ color: resolvedAccentColor, fontSize: "0.95rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>−{formatCurrency(upfrontSavings)}</Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
        <Typography sx={{ color: colors.grey[300], fontSize: "0.95rem" }}>Subtotal</Typography>
        <Typography sx={{ color: colors.grey[100], fontWeight: 700 }}>{formatCurrency(discountedSubtotal)}</Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
        <Typography sx={{ color: colors.grey[300], fontSize: "0.95rem" }}>Shipping</Typography>
        <Typography sx={{ color: colors.grey[100], fontWeight: 700 }}>{shippingLabel || (shippingCost != null ? formatCurrency(shippingCost) : "—")}</Typography>
      </Box>

      <Divider sx={{ borderColor: colors.grey[800], my: 2 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: actionLabel ? 2 : 0 }}>
        <Typography sx={{ color: useUpfrontPrice ? resolvedAccentColor : colors.grey[100], fontWeight: 800, fontSize: "1.05rem" }}>{totalLabel}</Typography>
        <Typography sx={{ color: useUpfrontPrice ? resolvedAccentColor : colors.grey[100], fontWeight: 800, fontSize: "1.5rem" }}>{formatCurrency(finalTotal)}</Typography>
      </Box>

      {useUpfrontPrice && finalSavings > 0 && upfrontSavings === 0 && (
        <Typography sx={{ color: colors.grey[400], fontSize: "0.85rem", mt: 1 }}>Save {formatCurrency(finalSavings)} by paying upfront.</Typography>
      )}

      {fullHeight && <Box sx={{ flex: 1 }} />}

      {actionLabel && (
        <Button fullWidth onClick={handlePrimaryClick} startIcon={actionIcon} sx={{ mt: 2, py: 1.5, backgroundColor: useUpfrontPrice ? resolvedAccentColor : undefined, color: useUpfrontPrice ? "#000" : undefined, fontWeight: 700, "&:hover": { backgroundColor: useUpfrontPrice ? `${resolvedAccentColor}dd` : undefined } }}>
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

export default OrderCalculationCard;
